export type RouteAccessPath = {
    pattern: string;
    matchType: "exact" | "prefix" | string;
};

export type RouteAccessPolicy = {
    restricted: boolean;
    permitted: boolean;
    allowedPaths: RouteAccessPath[];
    redirectTo: string | null;
    message: string | null;
};

class SysUser {
    id: string;
    name: string;
    preferredLanguage: string | null;
    timeZone: string | null;
    workInboxScope: "mine" | "mine_and_groups";
    dateTimeFormat: string | null;
    numberFormat: string | null;
    email: string;
    createdAt: Date | null;
    updatedAt: Date | null;
    jti: string | null;
    currentPlatformPackageId: string | null;
    themeId: string | null;
    isAdmin: boolean;
    roles: string[];
    groups: string[];
    routeAccess: RouteAccessPolicy;

    constructor(params: {
        id?: string;
        name?: string;
        preferredLanguage?: string | null;
        timeZone?: string | null;
        workInboxScope?: "mine" | "mine_and_groups";
        dateTimeFormat?: string | null;
        numberFormat?: string | null;
        email?: string;
        createdAt?: Date | string | null;
        updatedAt?: Date | string | null;
        jti?: string | null;
        currentPlatformPackageId?: string | null;
        themeId?: string | null;
        isAdmin?: boolean;
        roles?: string[];
        groups?: string[];
        routeAccess?: RouteAccessPolicy;
    } = {}) {
        this.id = params.id || "";
        this.name = params.name || "";
        this.preferredLanguage = params.preferredLanguage ?? null;
        this.timeZone = params.timeZone ?? null;
        this.workInboxScope = params.workInboxScope ?? "mine_and_groups";
        this.dateTimeFormat = params.dateTimeFormat ?? null;
        this.numberFormat = params.numberFormat ?? null;
        this.email = params.email || "";
        this.createdAt = params.createdAt ? new Date(params.createdAt) : null;
        this.updatedAt = params.updatedAt ? new Date(params.updatedAt) : null;
        this.jti = params.jti ?? null;
        this.currentPlatformPackageId = params.currentPlatformPackageId ?? null;
        this.themeId = params.themeId ?? null;
        this.isAdmin = params.isAdmin ?? false;
        this.roles = params.roles ?? [];
        this.groups = params.groups ?? [];
        this.routeAccess = params.routeAccess ?? {
            restricted: false,
            permitted: true,
            allowedPaths: [],
            redirectTo: null,
            message: null
        };
    }

    // Retorna o JSON no formato da API Rails
    toJSON(): any {
        return {
            id: this.id,
            name: this.name,
            preferred_language: this.preferredLanguage,
            time_zone: this.timeZone,
            work_inbox_scope: this.workInboxScope,
            date_time_format: this.dateTimeFormat,
            number_format: this.numberFormat,
            email: this.email,
            created_at: this.createdAt?.toISOString() ?? null,
            updated_at: this.updatedAt?.toISOString() ?? null,
            jti: this.jti,
            current_platform_package_id: this.currentPlatformPackageId,
            theme_id: this.themeId,
            is_admin: this.isAdmin,
            roles: this.roles,
            groups: this.groups,
            route_access: {
                restricted: this.routeAccess.restricted,
                permitted: this.routeAccess.permitted,
                allowed_paths: this.routeAccess.allowedPaths.map((path) => ({
                    pattern: path.pattern,
                    match_type: path.matchType
                })),
                redirect_to: this.routeAccess.redirectTo,
                message: this.routeAccess.message
            }
        };
    }

    fromJson(json: any): SysUser {
        this.id = json.id || "";
        this.name = json.name || "";
        this.preferredLanguage = json.preferred_language ?? null;
        this.timeZone = json.time_zone ?? null;
        this.workInboxScope = json.work_inbox_scope ?? "mine_and_groups";
        this.dateTimeFormat = json.date_time_format ?? null;
        this.numberFormat = json.number_format ?? null;
        this.email = json.email || "";
        this.createdAt = json.created_at ? new Date(json.created_at) : null;
        this.updatedAt = json.updated_at ? new Date(json.updated_at) : null;
        this.jti = json.jti ?? null;
        this.currentPlatformPackageId = json.current_platform_package_id ?? null;
        this.themeId = json.theme_id ?? null;
        this.isAdmin = json.is_admin === true;
        this.roles = Array.isArray(json.roles) ? json.roles : [];
        this.groups = Array.isArray(json.groups) ? json.groups : [];
        this.routeAccess = this.parseRouteAccess(json.route_access);
        return this;
    }

    private parseRouteAccess(json: any): RouteAccessPolicy {
        if (!json) {
            return {
                restricted: false,
                permitted: true,
                allowedPaths: [],
                redirectTo: null,
                message: null
            };
        }

        return {
            restricted: Boolean(json.restricted),
            permitted: json.permitted !== false,
            allowedPaths: Array.isArray(json.allowed_paths)
                ? json.allowed_paths.map((path: any) => ({
                    pattern: String(path.pattern || ""),
                    matchType: String(path.match_type || "exact")
                }))
                : [],
            redirectTo: json.redirect_to || null,
            message: json.message || null
        };
    }
}

export default SysUser;
